import { FaroConfig, parseBooleanConfig } from '../config';
import { BrowserCacheLocation, Configuration, LogLevel } from '@azure/msal-browser';
import { BASE_URL } from './utils';

export const ENV_LOCAL = 'local';
export const ENV_E2E = 'e2e';

export const readCommonConfig = () => ({
  env: process.env.REACT_APP_ENV ?? ENV_LOCAL,
  version: process.env.REACT_APP_VERSION ?? '0000000000000000000000000000000000000000',
  backendApiUrl: process.env.REACT_APP_BACKEND_API!!,
  inAppSignup: process.env.REACT_APP_IN_APP_SIGNUP === 'true',
  externalSignupUrl: process.env.REACT_APP_EXTERNAL_SIGNUP_URL,
  useSameTab: parseBooleanConfig(process.env.REACT_APP_USE_SAME_TAB),
});

export const googleConfig = {
  enabled: process.env.REACT_APP_GOOGLE_ENABLED === 'true',
  restrictedDomainsOnly: process.env.REACT_APP_GOOGLE_RESTRICTED_DOMAINS_ONLY?.split(','),
  clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID ?? '',
  calendarHelpUrl: process.env.REACT_APP_GOOGLE_CALENDAR_HELP_URL ?? 'https://growegy.com/',
};

export const outlookConfig = {
  enabled: process.env.REACT_APP_OUTLOOK_ENABLED === 'true',
  restrictedDomainsOnly: process.env.REACT_APP_OUTLOOK_RESTRICTED_DOMAINS_ONLY?.split(','),
  clientId: process.env.REACT_APP_OUTLOOK_CLIENT_ID ?? '',
  calendarHelpUrl: process.env.REACT_APP_OUTLOOK_HELP_URL ?? 'https://growegy.com/',
  redirectPath: '/ms-auth',
};

export const msalConfig: Configuration = {
  auth: {
    clientId: outlookConfig.clientId,
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: `${BASE_URL}${outlookConfig.redirectPath}`,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.MemoryStorage,
    temporaryCacheLocation: BrowserCacheLocation.MemoryStorage,
    storeAuthStateInCookie: false,
    secureCookies: true,
    cacheMigrationEnabled: false,
    claimsBasedCachingEnabled: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            logger.error(message);
            return;
          case LogLevel.Info:
            logger.info(message);
            return;
          case LogLevel.Verbose:
            logger.info(message);
            return;
          case LogLevel.Warning:
            logger.warn(message);
            return;
        }
      },
    },
  },
};

export const growegyAIConfig = {
  enabled: process.env.REACT_APP_GROWEGY_AI_ENABLED === 'true',
  libraryEnabled: process.env.REACT_APP_GROWEGY_AI_LIBRARY_ENABLED === 'true',
  variablesEnabled: process.env.REACT_APP_GROWEGY_AI_VARIABLES_ENABLED === 'true',
  recentlyUsedPromptsEnabled:
    process.env.REACT_APP_GROWEGY_AI_RECENTLY_USED_PROMPTS_ENABLED === 'true',
  maxMessagesPerChat: process.env.REACT_APP_GROWEGY_AI_MAX_MESSAGES_PER_CHAT
    ? +process.env.REACT_APP_GROWEGY_AI_MAX_MESSAGES_PER_CHAT
    : 25,
  programsChatEnabled: process.env.REACT_APP_PROGRAMS_CHAT_ENABLED === 'true',
};

export const programsConfig = {
  customFormulaFieldHelpUrl:
    process.env.REACT_APP_CUSTOM_FORMULA_FIELD_HELP_URL ?? 'https://www.growegy.com/formulas/',
  isCustomFieldFormulaEnabled: parseBooleanConfig(
    process.env.REACT_APP_PROGRAM_CUSTOM_FIELD_FORMULA_ENABLED
  ),
};

export const readFaroConfig = () => ({
  url: FaroConfig.url,
  apiKey: FaroConfig.apiKey,
  environment: readCommonConfig().env,
  version: readCommonConfig().version,
});

export const billingPortalConfig = {
  enabled: process.env.REACT_APP_BILLING_PORTAL_ENABLED === 'true',
  loyaltyBenefitsConsultationUrl:
    process.env.REACT_APP_BILLING_PORTAL_LOYALTY_BENEFITS_CONSULTATION_URL ??
    'https://growegy.com/small-business/loyalty-program/',
  loyaltyBenefitsCancellationConsultationUrl:
    process.env.REACT_APP_BILLING_PORTAL_LOYALTY_BENEFITS_CANCELLATION_CONSULTATION_URL ??
    'https://growegy.com/small-business/loyalty-program/',
  isSmsNotificationsFeatureEnabled: parseBooleanConfig(
    process.env.REACT_APP_BILLING_PORTAL_SHOW_SMS_NOTIFICATIONS_SWITCH
  ),
  payAllEnabled: parseBooleanConfig(process.env.REACT_APP_BILLING_PORTAL_PAY_ALL_ENABLED),
  payAllRolloutLevel: parseInt(process.env.REACT_APP_BILLING_PORTAL_PAY_ALL_ROLLOUT_LEVEL ?? '0'),
  isReferralLinkFeatureEnabled: parseBooleanConfig(
    process.env.REACT_APP_BILLING_PORTAL_REFERRAL_LINK_FEATURE_ENABLED
  ),
  showBillingPortalInIos: parseBooleanConfig(process.env.REACT_APP_SHOW_BILLING_PORTAL_IN_IOS),
  pauseOnCancellationRolloutLevel: parseInt(
    process.env.REACT_APP_BILLING_PORTAL_PAUSE_ON_CANCELLATION_ROLLOUT_LEVEL ?? '0'
  ),
};

export const PUSH_NOTIFICATIONS_GRANTED = 'granted';
export const PUSH_NOTIFICATIONS_UNDETERMINED = 'undetermined';
export const PUSH_NOTIFICATIONS_DENIED = 'denied';

declare global {
  // noinspection JSUnusedGlobalSymbols
  interface Window {
    isGrowegyMobile?: boolean;
    growegyMobilePlatform?: 'ios' | 'android';
  }
}

export const mobileConfig = () => ({
  isMobile: !!window.isGrowegyMobile,
  growegyMobilePlatform: window.growegyMobilePlatform ? window.growegyMobilePlatform : null,
});

export const businessPlanConfig = {
  enabled: parseBooleanConfig(process.env.REACT_APP_BUSINESS_PLAN_ENABLED),
  consultationUrl: process.env.REACT_APP_BUSINESS_PLAN_CONSULTATION_URL ?? 'https://growegy.com/',
};

export const marketplaceConfig = {
  enabled: parseBooleanConfig(process.env.REACT_APP_MARKETPLACE_ENABLED),
};

export const pushNotificationsConfig = {
  isEnabled: parseBooleanConfig(process.env.REACT_APP_ENABLE_PUSH_NOTIFICATIONS),
};

export const fundingConfig = {
  enabled: parseBooleanConfig(process.env.REACT_APP_FUNDING_ENABLED),
  fundingConsultationUrl: process.env.REACT_APP_FUNDING_CONSULTATION_URL ?? 'https://growegy.com/',
};

export const facebookConfig = {
  enabled: parseBooleanConfig(process.env.REACT_APP_FACEBOOK_ENABLED),
  appId: process.env.REACT_APP_FACEBOOK_APP_ID ?? '',
  helpUrl: process.env.REACT_APP_FACEBOOK_HELP_URL ?? 'https://growegy.com/',
  restrictedDomainsOnly: process.env.REACT_APP_FACEBOOK_RESTRICTED_DOMAINS_ONLY?.length
    ? process.env.REACT_APP_FACEBOOK_RESTRICTED_DOMAINS_ONLY.split(',')
    : [],
};

export const instagramConfig = {
  enabled: parseBooleanConfig(process.env.REACT_APP_INSTAGRAM_ENABLED),
  helpUrl: process.env.REACT_APP_FACEBOOK_HELP_URL ?? 'https://growegy.com/',
  redirectUrl: `${window.location.origin}/settings/team/integrations`,
  embedUrl: `https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=${process.env.REACT_APP_INSTAGRAM_APP_ID}&redirect_uri=${window.location.origin}/settings/team/integrations&response_type=code&scope=instagram_business_basic%2Cinstagram_business_content_publish`,
  restrictedDomainsOnly: process.env.REACT_APP_INSTAGRAM_RESTRICTED_DOMAINS_ONLY?.length
    ? process.env.REACT_APP_INSTAGRAM_RESTRICTED_DOMAINS_ONLY.split(',')
    : [],
};

export const youtubeConfig = {
  enabled: parseBooleanConfig(process.env.REACT_APP_YOUTUBE_ENABLED),
  redirectUrl: `${window.location.origin}/settings/team/integrations`,
  restrictedDomainsOnly: process.env.REACT_APP_YOUTUBE_RESTRICTED_DOMAINS_ONLY?.length
    ? process.env.REACT_APP_YOUTUBE_RESTRICTED_DOMAINS_ONLY.split(',')
    : [],
};
